import { uid } from "./helpers";

function looksLikeUrl(s) {
  if (!s) return false;
  const t = s.trim().toLowerCase();
  return t.startsWith("http://") || t.startsWith("https://") || t.startsWith("/") || t.includes(".") && t.includes("/");
}

export function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  // Detect separator: tab first, then semicolon, then comma
  const firstLine = lines[0];
  const sep = firstLine.includes("\t") ? "\t" : firstLine.includes(";") ? ";" : ",";
  const hdr = firstLine.split(sep).map(h => h.trim().toLowerCase().replace(/['"]/g, ""));
  const ki = hdr.findIndex(h => ["keyword","key","query","ключ","ключевое","запрос","ключове слово","запит"].includes(h));
  const fi = hdr.findIndex(h => ["frequency","freq","volume","частотність","частота","частотность","об'єм","объем","search volume"].includes(h));
  const ui = hdr.findIndex(h => ["url","page","сторінка","страница","landing","адрес","адреса","link","посилання","ссылка"].includes(h));
  if (ki < 0 || fi < 0 || ui < 0) return null;
  const rows = [];
  const mx = Math.max(ki, fi, ui);
  let skipped = 0;
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const c = line.split(sep).map(s => s.trim().replace(/^['"]|['"]$/g, ""));
    if (c.length <= mx) { skipped++; continue; }
    const kw = c[ki], fr = parseInt(c[fi], 10), url = c[ui];
    if (!kw || isNaN(fr)) { skipped++; continue; }
    // Validate URL — if it doesn't look like URL, skip the row
    if (!looksLikeUrl(url)) { skipped++; continue; }
    rows.push({ keyword: kw, frequency: fr, url: url.trim() });
  }
  if (skipped > 0) console.log(`[CSV] Parsed ${rows.length} rows, skipped ${skipped} invalid rows`);
  return rows;
}

// Robust parser for PASTED semantics ("ключ  частотність  url" per line).
// Позиційне розбиття ламалось, бо ключ містить пробіли, а роздільники змішані (таб/пробіли/;).
// Тому шукаємо колонки за ПАТЕРНОМ: спершу URL, потім частотність (останнє число), решта — ключ.
// URL-less рядки НЕ кидаємо на головну "/", а пропускаємо (це й був баг: ключі падали на homepage).
export function parsePastedRows(text) {
  const lines = String(text || "").trim().split(/\r?\n/);
  const rows = [];
  let skipped = 0, noUrl = 0;
  for (const raw of lines) {
    let line = String(raw || "").trim();
    if (!line) continue;
    // 1. URL: http(s):// | domain.tld/path | /root-relative-slug (не голий "/").
    const um = line.match(/https?:\/\/[^\s;,"']+/i)
          || line.match(/[a-z0-9][a-z0-9.\-]*\.[a-z]{2,}\/[^\s;,"']*/i)
          || line.match(/(?:^|[\s;,\t])(\/[a-z0-9][^\s;,"']*)/i);
    let url = "";
    if (um) {
      url = (um[1] || um[0]).trim().replace(/[.,;]+$/, "");
      line = (line.slice(0, um.index) + " " + line.slice(um.index + um[0].length)).trim();
    }
    // 2. Частотність = ОСТАННЄ число в залишку (URL уже прибрано, тож його цифри не заважають).
    const nums = [...line.matchAll(/\d[\d\s., ]*/g)];
    let fr = NaN;
    if (nums.length) {
      const lm = nums[nums.length - 1];
      fr = parseInt(String(lm[0]).replace(/[^\d]/g, ""), 10);
      line = (line.slice(0, lm.index) + " " + line.slice(lm.index + lm[0].length)).trim();
    }
    // 3. Ключ = решта.
    const kw = line.replace(/[\t;,]+/g, " ").replace(/\s{2,}/g, " ").trim();
    if (!kw || isNaN(fr)) { skipped++; continue; }
    if (!url) { noUrl++; continue; }
    rows.push({ keyword: kw, frequency: fr, url });
  }
  return { rows, skipped, noUrl };
}

export function buildClusters(rows) {
  const m = {};
  rows.forEach(r => { if (!m[r.url]) m[r.url] = []; m[r.url].push({ keyword: r.keyword, frequency: r.frequency }); });
  return Object.entries(m).map(([url, kws]) => {
    kws.sort((a, b) => b.frequency - a.frequency);
    return {
      id: uid(), url, keywords: kws, allCount: kws.length,
      hfKeyword: kws[0]?.keyword || "", hfFreq: kws[0]?.frequency || 0,
      status: kws.length >= 3 ? "READY" : "NOT_ENOUGH_KEYWORDS",
      meta: null, issues: [], error: ""
    };
  }).sort((a, b) => b.allCount - a.allCount);
}

// Merge new rows into existing clusters — preserves meta, content, status
export function mergeClusters(existingClusters, newRows) {
  // Index existing clusters by URL
  const existingByUrl = {};
  existingClusters.forEach(c => {
    existingByUrl[c.url.replace(/\/+$/, "")] = c;
  });

  // Group new rows by URL
  const newByUrl = {};
  newRows.forEach(r => {
    const norm = r.url.replace(/\/+$/, "");
    if (!newByUrl[norm]) newByUrl[norm] = [];
    newByUrl[norm].push({ keyword: r.keyword, frequency: r.frequency });
  });

  const updated = [];
  const processedUrls = new Set();

  // Update existing clusters with new keywords
  existingClusters.forEach(c => {
    const norm = c.url.replace(/\/+$/, "");
    processedUrls.add(norm);
    const newKws = newByUrl[norm];
    if (newKws) {
      // Merge keywords — add new, update frequency of existing
      const kwMap = {};
      (c.keywords || []).forEach(k => { kwMap[k.keyword.toLowerCase()] = k; });
      newKws.forEach(k => {
        const key = k.keyword.toLowerCase();
        if (kwMap[key]) {
          // Update frequency if new is higher
          if (k.frequency > kwMap[key].frequency) kwMap[key].frequency = k.frequency;
        } else {
          kwMap[key] = k;
        }
      });
      const allKws = Object.values(kwMap).sort((a, b) => b.frequency - a.frequency);
      updated.push({
        ...c, // preserve id, meta, content, status, issues
        keywords: allKws,
        allCount: allKws.length,
        hfKeyword: allKws[0]?.keyword || c.hfKeyword,
        hfFreq: allKws[0]?.frequency || c.hfFreq,
      });
    } else {
      // No new data for this URL — keep as is
      updated.push(c);
    }
  });

  // Add completely new URLs
  let newCount = 0;
  Object.entries(newByUrl).forEach(([url, kws]) => {
    if (!processedUrls.has(url)) {
      kws.sort((a, b) => b.frequency - a.frequency);
      updated.push({
        id: uid(),
        url: url,
        keywords: kws,
        allCount: kws.length,
        hfKeyword: kws[0]?.keyword || "",
        hfFreq: kws[0]?.frequency || 0,
        status: kws.length >= 3 ? "READY" : "NOT_ENOUGH_KEYWORDS",
        meta: null, issues: [], error: "",
      });
      newCount++;
    }
  });

  return { clusters: updated.sort((a, b) => b.allCount - a.allCount), newUrls: newCount, updatedUrls: Object.keys(newByUrl).length - newCount };
}
